import { Controller } from "@hotwired/stimulus"

// One slide-over panel with three views (list, new, edit). Every server response is a
// turbo stream that replaces #school_tasks_quick_look (and refreshes the badge / page list).
export default class extends Controller {

  async open(event) {
    event.preventDefault();
    await this.load("/schools/tasks/quick-look");
    this.panel().classList.remove("u-hidden");
    this.panel().querySelector(".Quicklook")?.classList.add("slide-in-animation");
  }

  close(event) {
    event.preventDefault();
    this.panel().classList.add("u-hidden");
  }

  showList(event) {
    event.preventDefault();
    this.load("/schools/tasks/quick-look");
  }

  async showNew(event) {
    event.preventDefault();
    await this.load("/schools/tasks/quick-look/add");
    this.panel().classList.remove("u-hidden");
  }

  async showEdit(event) {
    event.preventDefault();
    await this.load(`/schools/tasks/quick-look/${event.currentTarget.dataset.id}`);
    this.panel().classList.remove("u-hidden");
  }

  async toggleComplete(event) {
    event.preventDefault();
    const button = event.currentTarget;
    button.disabled = true;

    const response = await this.performFetch(`/schools/tasks/quick-look/${button.dataset.id}/complete`, "PATCH");
    if (response.ok) {
      Turbo.renderStreamMessage(await response.text());
    } else {
      button.disabled = false;
      alert("Sorry, that task couldn't be updated. Please try again.");
    }
  }

  async submit(event) {
    event.preventDefault();

    const errors = this.validateForm();
    if (errors.length > 0) {
      this.showErrorPanel(errors.join("<br>"));
      return;
    }

    this.toggleLoading(true);
    const form = event.target.closest("form");
    const response = await this.performFetch(form.action, "POST", new FormData(form));
    await this.handleResponse(response);
    this.toggleLoading(false);
  }

  async confirmDelete(event) {
    event.preventDefault();
    const response = await this.performFetch(`/schools/tasks/quick-look/${event.currentTarget.dataset.id}/delete`, "PATCH");
    await this.handleResponse(response);
  }

  // Picking a due date fills an empty time with 5pm. A time that's already there is left
  // alone, whether it came from the saved task or the user typed it.
  dueDateChanged(event) {
    const dueTime = document.getElementById("task_due_time");
    if (!dueTime || !event.currentTarget.value || dueTime.value) return;

    dueTime.value = "17:00";
  }

  showDeletePanel(event) {
    event.preventDefault();
    document.getElementById("delete-confirmation-panel").removeAttribute("hidden");
  }

  hideDeletePanel(event) {
    event.preventDefault();
    document.getElementById("delete-confirmation-panel").setAttribute("hidden", true);
  }

  // --- helpers ---

  // The panel is rendered inside the sidebar, which is position: sticky and so traps the
  // fixed Quicklook in its stacking context (and hides it on mobile). Lift it onto <body>.
  panel() {
    const panel = document.getElementById("school-tasks-quick-look");
    if (panel && panel.parentElement !== document.body) {
      document.body.appendChild(panel);
    }
    return panel;
  }

  async load(url) {
    const response = await this.performFetch(url, "GET");
    if (response.ok) {
      Turbo.renderStreamMessage(await response.text());
    }
  }

  async handleResponse(response) {
    if (response.ok) {
      Turbo.renderStreamMessage(await response.text());
      return;
    }
    if (response.error) {
      this.showErrorPanel(response.error);
      return;
    }

    try {
      const body = await response.json();
      this.showErrorPanel((body.errors || [body.error]).join("<br>"));
    } catch (e) {
      this.showErrorPanel("Something has gone wrong. Please try again later.");
    }
  }

  validateForm() {
    const errors = [];
    const title = document.getElementById("task_title");
    const assignee = document.getElementById("task_assignee");
    const dueDate = document.getElementById("task_due_date");
    const dueTime = document.getElementById("task_due_time");

    if (title && title.value.trim() === "") errors.push("Title cannot be empty.");
    if (assignee && assignee.value === "") errors.push("Choose who the task is assigned to: Anyone, Unassigned or a team member.");
    if (dueDate && dueTime && (dueDate.value === "") !== (dueTime.value === "")) {
      errors.push("Set both a due date and time, or leave both blank.");
    }
    return errors;
  }

  // Rails reads the _method hidden field for PATCH, so forms always go as POST.
  async performFetch(url, method, body = null) {
    url = this.withPageView(url);
    const options = {
      method: method,
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "text/vnd.turbo-stream.html"
      }
    };
    if (body) options.body = body;

    try {
      return await fetch(url, options);
    } catch (error) {
      console.error("Error performing fetch: ", error);
      return { error: "Network error, please check your connection and try again." };
    }
  }

  // On the My Tasks page, pass its tab/filter so the server re-renders the page list in the same view.
  withPageView(url) {
    const pageList = document.getElementById("school_tasks_page_list");
    if (!pageList) return url;

    const params = new URLSearchParams({ page_tab: pageList.dataset.tab, page_filter: pageList.dataset.filter });
    return `${url}${url.includes("?") ? "&" : "?"}${params}`;
  }

  showErrorPanel(message) {
    const errorPanel = document.getElementById("error-panel");
    const errorMessage = document.getElementById("error-message");
    if (!errorPanel || !errorMessage) return;

    errorMessage.innerHTML = message;
    errorPanel.hidden = false;
    errorMessage.hidden = false;
  }

  toggleLoading(show) {
    const formFields = document.getElementById("form-content");
    const loader = document.getElementById("loader");
    if (!formFields || !loader) return;

    loader.style.display = show ? "flex" : "none";
    formFields.classList.toggle("hidden", show);

    document.querySelectorAll("#school_tasks_quick_look form button").forEach(button => {
      button.disabled = show;
      button.classList.toggle("Button--disabled", show);
    });
  }
}
